import { useQuery } from "@tanstack/react-query";
import { facilityApi } from "@bw/shared/api";
import type { FacilityResponse } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

/**
 * The facility row (GET /facilities/{id} → FacilityResponse), read by the drop-in
 * journey for its `queue_assignment_mode` and `timezone`. Assignment mode is
 * the server-owned flag that decides which post-check-in path a player gets:
 *   - "self_serve" → the court board (start/join/leave a game yourself);
 *   - "auto" / "manual" → the court QUEUE (join the line, staff/auto assign).
 * Facility timezone keeps session-expiry facts aligned with operator surfaces.
 *
 * Module isolation: the booking module's `useFacility` can't be imported here, so
 * the drop-in journey owns this thin read against its own cache slot.
 */
export function useFacilityMode(
  facilityId: string,
  enabled: boolean = true,
) {
  return useQuery<FacilityResponse>({
    queryKey: queryKeys.dropin.facility(facilityId),
    queryFn: () => facilityApi.get(facilityId),
    enabled: Boolean(facilityId) && enabled,
    staleTime: 5 * 60_000,
  });
}
